// US geographic area codes (NANP) — the shared source of truth for the
// `preferred_phone_number_prefixes` feature (PE-1766).
//
// Consumed by BOTH sides of the wire so they can never disagree:
//   - dashboard: the SMS-settings area-code combobox (search by digits or
//     place name; manual entry only accepted if the code exists here)
//   - marketing backend: the Zod refinement on requestSmsNumber's
//     `preferredPrefixes` (FE-only validation would leave the API accepting
//     fake codes sent directly)
//
// Scope: geographic codes for the 50 states + DC only. Deliberately excluded:
//   - toll-free (800/833/844/855/866/877/888) and premium (900) — not valid
//     targets for a local 10DLC sending number
//   - non-geographic/special codes (500, 521-577 PCS blocks, 600, 700, 710)
//   - US territories (PR 787/939, VI 340, Guam 671, AS 684, CNMI 670) —
//     Telgorithm provisions CONUS+AK+HI local numbers
//   - Canada and Caribbean NANP codes
//
// Maintenance: NANPA assigns a handful of new overlay codes per year
// (https://nanpa.com → NPA reports). Adding one is a one-line change here;
// the dataset test only sanity-checks shape, not membership. Grouped by
// state (matching NANPA's per-state listing) to make that diff review easy.

export type UsAreaCode = {
  // Three ASCII digits, e.g. "713".
  code: string;
  // Two-letter USPS state code, e.g. "TX". "DC" for the district.
  state: string;
  // Short human label for the combobox row — the principal city or region
  // the code serves, e.g. "Houston". Not exhaustive coverage description.
  region: string;
};

export const US_AREA_CODES: readonly UsAreaCode[] = [
  // Alabama
  { code: '205', state: 'AL', region: 'Birmingham' },
  { code: '251', state: 'AL', region: 'Mobile' },
  { code: '256', state: 'AL', region: 'Huntsville' },
  { code: '334', state: 'AL', region: 'Montgomery' },
  { code: '659', state: 'AL', region: 'Birmingham' },
  { code: '938', state: 'AL', region: 'Huntsville' },
  // Alaska
  { code: '907', state: 'AK', region: 'Anchorage / statewide' },
  // Arizona
  { code: '480', state: 'AZ', region: 'Mesa / East Valley' },
  { code: '520', state: 'AZ', region: 'Tucson' },
  { code: '602', state: 'AZ', region: 'Phoenix' },
  { code: '623', state: 'AZ', region: 'Glendale / West Valley' },
  { code: '928', state: 'AZ', region: 'Flagstaff / Yuma' },
  // Arkansas
  { code: '479', state: 'AR', region: 'Fort Smith / Fayetteville' },
  { code: '501', state: 'AR', region: 'Little Rock' },
  { code: '870', state: 'AR', region: 'Jonesboro / Pine Bluff' },
  // California
  { code: '209', state: 'CA', region: 'Stockton / Modesto' },
  { code: '213', state: 'CA', region: 'Los Angeles (downtown)' },
  { code: '279', state: 'CA', region: 'Sacramento' },
  { code: '310', state: 'CA', region: 'West Los Angeles' },
  { code: '323', state: 'CA', region: 'Los Angeles' },
  { code: '341', state: 'CA', region: 'Oakland / East Bay' },
  { code: '350', state: 'CA', region: 'Stockton / Modesto' },
  { code: '408', state: 'CA', region: 'San Jose' },
  { code: '415', state: 'CA', region: 'San Francisco' },
  { code: '424', state: 'CA', region: 'West Los Angeles' },
  { code: '442', state: 'CA', region: 'Oceanside / Palm Springs' },
  { code: '510', state: 'CA', region: 'Oakland / East Bay' },
  { code: '530', state: 'CA', region: 'Redding / Chico' },
  { code: '559', state: 'CA', region: 'Fresno' },
  { code: '562', state: 'CA', region: 'Long Beach' },
  { code: '619', state: 'CA', region: 'San Diego' },
  { code: '626', state: 'CA', region: 'Pasadena / San Gabriel Valley' },
  { code: '628', state: 'CA', region: 'San Francisco' },
  { code: '650', state: 'CA', region: 'San Mateo / Palo Alto' },
  { code: '657', state: 'CA', region: 'Anaheim / North Orange County' },
  { code: '661', state: 'CA', region: 'Bakersfield' },
  { code: '669', state: 'CA', region: 'San Jose' },
  { code: '707', state: 'CA', region: 'Santa Rosa / Napa' },
  { code: '714', state: 'CA', region: 'Anaheim / Orange County' },
  { code: '747', state: 'CA', region: 'San Fernando Valley' },
  { code: '760', state: 'CA', region: 'Oceanside / Palm Springs' },
  { code: '805', state: 'CA', region: 'Ventura / Santa Barbara' },
  { code: '818', state: 'CA', region: 'San Fernando Valley' },
  { code: '820', state: 'CA', region: 'Ventura / Santa Barbara' },
  { code: '831', state: 'CA', region: 'Monterey / Santa Cruz' },
  { code: '840', state: 'CA', region: 'San Bernardino' },
  { code: '858', state: 'CA', region: 'San Diego (north)' },
  { code: '909', state: 'CA', region: 'San Bernardino' },
  { code: '916', state: 'CA', region: 'Sacramento' },
  { code: '925', state: 'CA', region: 'Concord / Walnut Creek' },
  { code: '949', state: 'CA', region: 'Irvine / South Orange County' },
  { code: '951', state: 'CA', region: 'Riverside' },
  // Colorado
  { code: '303', state: 'CO', region: 'Denver' },
  { code: '719', state: 'CO', region: 'Colorado Springs' },
  { code: '720', state: 'CO', region: 'Denver' },
  { code: '970', state: 'CO', region: 'Fort Collins / Grand Junction' },
  { code: '983', state: 'CO', region: 'Denver' },
  // Connecticut
  { code: '203', state: 'CT', region: 'Bridgeport / New Haven' },
  { code: '475', state: 'CT', region: 'Bridgeport / New Haven' },
  { code: '860', state: 'CT', region: 'Hartford' },
  { code: '959', state: 'CT', region: 'Hartford' },
  // Delaware
  { code: '302', state: 'DE', region: 'Wilmington / statewide' },
  // District of Columbia
  { code: '202', state: 'DC', region: 'Washington' },
  { code: '771', state: 'DC', region: 'Washington' },
  // Florida
  { code: '239', state: 'FL', region: 'Fort Myers / Naples' },
  { code: '305', state: 'FL', region: 'Miami' },
  { code: '321', state: 'FL', region: 'Orlando / Space Coast' },
  { code: '352', state: 'FL', region: 'Gainesville / Ocala' },
  { code: '386', state: 'FL', region: 'Daytona Beach' },
  { code: '407', state: 'FL', region: 'Orlando' },
  { code: '448', state: 'FL', region: 'Tallahassee / Panhandle' },
  { code: '561', state: 'FL', region: 'West Palm Beach' },
  { code: '656', state: 'FL', region: 'Tampa' },
  { code: '689', state: 'FL', region: 'Orlando' },
  { code: '727', state: 'FL', region: 'St. Petersburg / Clearwater' },
  { code: '754', state: 'FL', region: 'Fort Lauderdale' },
  { code: '772', state: 'FL', region: 'Port St. Lucie' },
  { code: '786', state: 'FL', region: 'Miami' },
  { code: '813', state: 'FL', region: 'Tampa' },
  { code: '850', state: 'FL', region: 'Tallahassee / Pensacola' },
  { code: '863', state: 'FL', region: 'Lakeland' },
  { code: '904', state: 'FL', region: 'Jacksonville' },
  { code: '941', state: 'FL', region: 'Sarasota / Bradenton' },
  { code: '954', state: 'FL', region: 'Fort Lauderdale' },
  // Georgia
  { code: '229', state: 'GA', region: 'Albany' },
  { code: '404', state: 'GA', region: 'Atlanta' },
  { code: '470', state: 'GA', region: 'Atlanta metro' },
  { code: '478', state: 'GA', region: 'Macon' },
  { code: '678', state: 'GA', region: 'Atlanta metro' },
  { code: '706', state: 'GA', region: 'Augusta / Columbus' },
  { code: '762', state: 'GA', region: 'Augusta / Columbus' },
  { code: '770', state: 'GA', region: 'Atlanta suburbs' },
  { code: '912', state: 'GA', region: 'Savannah' },
  { code: '943', state: 'GA', region: 'Atlanta metro' },
  // Hawaii
  { code: '808', state: 'HI', region: 'Honolulu / statewide' },
  // Idaho
  { code: '208', state: 'ID', region: 'Boise / statewide' },
  { code: '986', state: 'ID', region: 'Boise / statewide' },
  // Illinois
  { code: '217', state: 'IL', region: 'Springfield / Champaign' },
  { code: '224', state: 'IL', region: 'Northern Chicago suburbs' },
  { code: '309', state: 'IL', region: 'Peoria / Bloomington' },
  { code: '312', state: 'IL', region: 'Chicago (downtown)' },
  { code: '331', state: 'IL', region: 'Western Chicago suburbs' },
  { code: '447', state: 'IL', region: 'Springfield / Champaign' },
  { code: '464', state: 'IL', region: 'Southern Chicago suburbs' },
  { code: '618', state: 'IL', region: 'Southern Illinois' },
  { code: '630', state: 'IL', region: 'Western Chicago suburbs' },
  { code: '708', state: 'IL', region: 'Southern Chicago suburbs' },
  { code: '773', state: 'IL', region: 'Chicago' },
  { code: '779', state: 'IL', region: 'Rockford / Joliet' },
  { code: '815', state: 'IL', region: 'Rockford / Joliet' },
  { code: '847', state: 'IL', region: 'Northern Chicago suburbs' },
  { code: '872', state: 'IL', region: 'Chicago' },
  // Indiana
  { code: '219', state: 'IN', region: 'Gary / Northwest Indiana' },
  { code: '260', state: 'IN', region: 'Fort Wayne' },
  { code: '317', state: 'IN', region: 'Indianapolis' },
  { code: '463', state: 'IN', region: 'Indianapolis' },
  { code: '574', state: 'IN', region: 'South Bend' },
  { code: '765', state: 'IN', region: 'Lafayette / Muncie' },
  { code: '812', state: 'IN', region: 'Evansville / Bloomington' },
  { code: '930', state: 'IN', region: 'Evansville / Bloomington' },
  // Iowa
  { code: '319', state: 'IA', region: 'Cedar Rapids / Iowa City' },
  { code: '515', state: 'IA', region: 'Des Moines' },
  { code: '563', state: 'IA', region: 'Davenport / Dubuque' },
  { code: '641', state: 'IA', region: 'Mason City / Ottumwa' },
  { code: '712', state: 'IA', region: 'Sioux City / Council Bluffs' },
  // Kansas
  { code: '316', state: 'KS', region: 'Wichita' },
  { code: '620', state: 'KS', region: 'Hutchinson / Dodge City' },
  { code: '785', state: 'KS', region: 'Topeka / Lawrence' },
  { code: '913', state: 'KS', region: 'Kansas City (KS)' },
  // Kentucky
  { code: '270', state: 'KY', region: 'Bowling Green / Paducah' },
  { code: '364', state: 'KY', region: 'Bowling Green / Paducah' },
  { code: '502', state: 'KY', region: 'Louisville' },
  { code: '606', state: 'KY', region: 'Eastern Kentucky' },
  { code: '859', state: 'KY', region: 'Lexington' },
  // Louisiana
  { code: '225', state: 'LA', region: 'Baton Rouge' },
  { code: '318', state: 'LA', region: 'Shreveport / Monroe' },
  { code: '337', state: 'LA', region: 'Lafayette / Lake Charles' },
  { code: '504', state: 'LA', region: 'New Orleans' },
  { code: '985', state: 'LA', region: 'Houma / Hammond' },
  // Maine
  { code: '207', state: 'ME', region: 'Portland / statewide' },
  // Maryland
  { code: '227', state: 'MD', region: 'Western Maryland / DC suburbs' },
  { code: '240', state: 'MD', region: 'Western Maryland / DC suburbs' },
  { code: '301', state: 'MD', region: 'Western Maryland / DC suburbs' },
  { code: '410', state: 'MD', region: 'Baltimore / Eastern Shore' },
  { code: '443', state: 'MD', region: 'Baltimore / Eastern Shore' },
  { code: '667', state: 'MD', region: 'Baltimore / Eastern Shore' },
  // Massachusetts
  { code: '339', state: 'MA', region: 'Boston suburbs' },
  { code: '351', state: 'MA', region: 'Lowell / Lawrence' },
  { code: '413', state: 'MA', region: 'Springfield / Western Mass' },
  { code: '508', state: 'MA', region: 'Worcester / Cape Cod' },
  { code: '617', state: 'MA', region: 'Boston' },
  { code: '774', state: 'MA', region: 'Worcester / Cape Cod' },
  { code: '781', state: 'MA', region: 'Boston suburbs' },
  { code: '857', state: 'MA', region: 'Boston' },
  { code: '978', state: 'MA', region: 'Lowell / Lawrence' },
  // Michigan
  { code: '231', state: 'MI', region: 'Muskegon / Traverse City' },
  { code: '248', state: 'MI', region: 'Oakland County' },
  { code: '269', state: 'MI', region: 'Kalamazoo / Battle Creek' },
  { code: '313', state: 'MI', region: 'Detroit' },
  { code: '517', state: 'MI', region: 'Lansing' },
  { code: '586', state: 'MI', region: 'Macomb County' },
  { code: '616', state: 'MI', region: 'Grand Rapids' },
  { code: '734', state: 'MI', region: 'Ann Arbor' },
  { code: '810', state: 'MI', region: 'Flint' },
  { code: '906', state: 'MI', region: 'Upper Peninsula' },
  { code: '947', state: 'MI', region: 'Oakland County' },
  { code: '989', state: 'MI', region: 'Saginaw / Midland' },
  // Minnesota
  { code: '218', state: 'MN', region: 'Duluth / northern Minnesota' },
  { code: '320', state: 'MN', region: 'St. Cloud' },
  { code: '507', state: 'MN', region: 'Rochester / Mankato' },
  { code: '612', state: 'MN', region: 'Minneapolis' },
  { code: '651', state: 'MN', region: 'St. Paul' },
  { code: '763', state: 'MN', region: 'Northwest Twin Cities suburbs' },
  { code: '952', state: 'MN', region: 'Southwest Twin Cities suburbs' },
  // Mississippi
  { code: '228', state: 'MS', region: 'Gulfport / Biloxi' },
  { code: '601', state: 'MS', region: 'Jackson' },
  { code: '662', state: 'MS', region: 'Tupelo / northern Mississippi' },
  { code: '769', state: 'MS', region: 'Jackson' },
  // Missouri
  { code: '314', state: 'MO', region: 'St. Louis' },
  { code: '417', state: 'MO', region: 'Springfield' },
  { code: '557', state: 'MO', region: 'St. Louis' },
  { code: '573', state: 'MO', region: 'Columbia / Jefferson City' },
  { code: '636', state: 'MO', region: 'St. Charles / west St. Louis metro' },
  { code: '660', state: 'MO', region: 'Sedalia / northern Missouri' },
  { code: '816', state: 'MO', region: 'Kansas City (MO)' },
  // Montana
  { code: '406', state: 'MT', region: 'Billings / statewide' },
  // Nebraska
  { code: '308', state: 'NE', region: 'Grand Island / western Nebraska' },
  { code: '402', state: 'NE', region: 'Omaha / Lincoln' },
  { code: '531', state: 'NE', region: 'Omaha / Lincoln' },
  // Nevada
  { code: '702', state: 'NV', region: 'Las Vegas' },
  { code: '725', state: 'NV', region: 'Las Vegas' },
  { code: '775', state: 'NV', region: 'Reno / rest of state' },
  // New Hampshire
  { code: '603', state: 'NH', region: 'Manchester / statewide' },
  // New Jersey
  { code: '201', state: 'NJ', region: 'Jersey City / Hackensack' },
  { code: '551', state: 'NJ', region: 'Jersey City / Hackensack' },
  { code: '609', state: 'NJ', region: 'Trenton / Atlantic City' },
  { code: '640', state: 'NJ', region: 'Trenton / Atlantic City' },
  { code: '732', state: 'NJ', region: 'New Brunswick / Toms River' },
  { code: '848', state: 'NJ', region: 'New Brunswick / Toms River' },
  { code: '856', state: 'NJ', region: 'Camden / southern New Jersey' },
  { code: '862', state: 'NJ', region: 'Newark / Paterson' },
  { code: '908', state: 'NJ', region: 'Elizabeth / western New Jersey' },
  { code: '973', state: 'NJ', region: 'Newark / Paterson' },
  // New Mexico
  { code: '505', state: 'NM', region: 'Albuquerque / Santa Fe' },
  { code: '575', state: 'NM', region: 'Las Cruces / Roswell' },
  // New York
  { code: '212', state: 'NY', region: 'New York City (Manhattan)' },
  { code: '315', state: 'NY', region: 'Syracuse / Utica' },
  { code: '332', state: 'NY', region: 'New York City (Manhattan)' },
  { code: '347', state: 'NY', region: 'New York City (outer boroughs)' },
  { code: '363', state: 'NY', region: 'Long Island (Nassau)' },
  { code: '516', state: 'NY', region: 'Long Island (Nassau)' },
  { code: '518', state: 'NY', region: 'Albany' },
  { code: '585', state: 'NY', region: 'Rochester' },
  { code: '607', state: 'NY', region: 'Binghamton / Ithaca' },
  { code: '631', state: 'NY', region: 'Long Island (Suffolk)' },
  { code: '646', state: 'NY', region: 'New York City (Manhattan)' },
  { code: '680', state: 'NY', region: 'Syracuse / Utica' },
  { code: '716', state: 'NY', region: 'Buffalo' },
  { code: '718', state: 'NY', region: 'New York City (outer boroughs)' },
  { code: '838', state: 'NY', region: 'Albany' },
  { code: '845', state: 'NY', region: 'Hudson Valley' },
  { code: '914', state: 'NY', region: 'Westchester' },
  { code: '917', state: 'NY', region: 'New York City' },
  { code: '929', state: 'NY', region: 'New York City (outer boroughs)' },
  { code: '934', state: 'NY', region: 'Long Island (Suffolk)' },
  // North Carolina
  { code: '252', state: 'NC', region: 'Greenville / Outer Banks' },
  { code: '336', state: 'NC', region: 'Greensboro / Winston-Salem' },
  { code: '704', state: 'NC', region: 'Charlotte' },
  { code: '743', state: 'NC', region: 'Greensboro / Winston-Salem' },
  { code: '828', state: 'NC', region: 'Asheville' },
  { code: '910', state: 'NC', region: 'Wilmington / Fayetteville' },
  { code: '919', state: 'NC', region: 'Raleigh / Durham' },
  { code: '980', state: 'NC', region: 'Charlotte' },
  { code: '984', state: 'NC', region: 'Raleigh / Durham' },
  // North Dakota
  { code: '701', state: 'ND', region: 'Fargo / statewide' },
  // Ohio
  { code: '216', state: 'OH', region: 'Cleveland' },
  { code: '220', state: 'OH', region: 'Newark / southeastern Ohio' },
  { code: '234', state: 'OH', region: 'Akron / Youngstown' },
  { code: '283', state: 'OH', region: 'Cincinnati' },
  { code: '326', state: 'OH', region: 'Dayton' },
  { code: '330', state: 'OH', region: 'Akron / Youngstown' },
  { code: '380', state: 'OH', region: 'Columbus' },
  { code: '419', state: 'OH', region: 'Toledo' },
  { code: '440', state: 'OH', region: 'Cleveland suburbs' },
  { code: '513', state: 'OH', region: 'Cincinnati' },
  { code: '567', state: 'OH', region: 'Toledo' },
  { code: '614', state: 'OH', region: 'Columbus' },
  { code: '740', state: 'OH', region: 'Newark / southeastern Ohio' },
  { code: '937', state: 'OH', region: 'Dayton' },
  // Oklahoma
  { code: '405', state: 'OK', region: 'Oklahoma City' },
  { code: '539', state: 'OK', region: 'Tulsa' },
  { code: '572', state: 'OK', region: 'Oklahoma City' },
  { code: '580', state: 'OK', region: 'Lawton / rural Oklahoma' },
  { code: '918', state: 'OK', region: 'Tulsa' },
  // Oregon
  { code: '458', state: 'OR', region: 'Eugene / southern Oregon' },
  { code: '503', state: 'OR', region: 'Portland / Salem' },
  { code: '541', state: 'OR', region: 'Eugene / southern Oregon' },
  { code: '971', state: 'OR', region: 'Portland / Salem' },
  // Pennsylvania
  { code: '215', state: 'PA', region: 'Philadelphia' },
  { code: '223', state: 'PA', region: 'Harrisburg / Lancaster' },
  { code: '267', state: 'PA', region: 'Philadelphia' },
  { code: '272', state: 'PA', region: 'Scranton / Wilkes-Barre' },
  { code: '412', state: 'PA', region: 'Pittsburgh' },
  { code: '445', state: 'PA', region: 'Philadelphia' },
  { code: '484', state: 'PA', region: 'Allentown / Reading' },
  { code: '570', state: 'PA', region: 'Scranton / Wilkes-Barre' },
  { code: '610', state: 'PA', region: 'Allentown / Reading' },
  { code: '717', state: 'PA', region: 'Harrisburg / Lancaster' },
  { code: '724', state: 'PA', region: 'Pittsburgh suburbs' },
  { code: '814', state: 'PA', region: 'Erie / Altoona' },
  { code: '878', state: 'PA', region: 'Pittsburgh' },
  // Rhode Island
  { code: '401', state: 'RI', region: 'Providence / statewide' },
  // South Carolina
  { code: '803', state: 'SC', region: 'Columbia' },
  { code: '839', state: 'SC', region: 'Columbia' },
  { code: '843', state: 'SC', region: 'Charleston / Myrtle Beach' },
  { code: '854', state: 'SC', region: 'Charleston / Myrtle Beach' },
  { code: '864', state: 'SC', region: 'Greenville / Spartanburg' },
  // South Dakota
  { code: '605', state: 'SD', region: 'Sioux Falls / statewide' },
  // Tennessee
  { code: '423', state: 'TN', region: 'Chattanooga / Tri-Cities' },
  { code: '615', state: 'TN', region: 'Nashville' },
  { code: '629', state: 'TN', region: 'Nashville' },
  { code: '731', state: 'TN', region: 'Jackson (TN)' },
  { code: '865', state: 'TN', region: 'Knoxville' },
  { code: '901', state: 'TN', region: 'Memphis' },
  { code: '931', state: 'TN', region: 'Clarksville / Cookeville' },
  // Texas
  { code: '210', state: 'TX', region: 'San Antonio' },
  { code: '214', state: 'TX', region: 'Dallas' },
  { code: '254', state: 'TX', region: 'Waco / Killeen' },
  { code: '281', state: 'TX', region: 'Houston' },
  { code: '325', state: 'TX', region: 'Abilene / San Angelo' },
  { code: '346', state: 'TX', region: 'Houston' },
  { code: '361', state: 'TX', region: 'Corpus Christi' },
  { code: '409', state: 'TX', region: 'Beaumont / Galveston' },
  { code: '430', state: 'TX', region: 'Tyler / Texarkana' },
  { code: '432', state: 'TX', region: 'Midland / Odessa' },
  { code: '469', state: 'TX', region: 'Dallas' },
  { code: '512', state: 'TX', region: 'Austin' },
  { code: '682', state: 'TX', region: 'Fort Worth' },
  { code: '713', state: 'TX', region: 'Houston' },
  { code: '726', state: 'TX', region: 'San Antonio' },
  { code: '737', state: 'TX', region: 'Austin' },
  { code: '806', state: 'TX', region: 'Lubbock / Amarillo' },
  { code: '817', state: 'TX', region: 'Fort Worth' },
  { code: '830', state: 'TX', region: 'New Braunfels / Hill Country' },
  { code: '832', state: 'TX', region: 'Houston' },
  { code: '903', state: 'TX', region: 'Tyler / Texarkana' },
  { code: '915', state: 'TX', region: 'El Paso' },
  { code: '936', state: 'TX', region: 'Conroe / Huntsville' },
  { code: '940', state: 'TX', region: 'Denton / Wichita Falls' },
  { code: '945', state: 'TX', region: 'Dallas metro' },
  { code: '956', state: 'TX', region: 'Laredo / Rio Grande Valley' },
  { code: '972', state: 'TX', region: 'Dallas metro' },
  { code: '979', state: 'TX', region: 'College Station' },
  // Utah
  { code: '385', state: 'UT', region: 'Salt Lake City' },
  { code: '435', state: 'UT', region: 'St. George / rural Utah' },
  { code: '801', state: 'UT', region: 'Salt Lake City' },
  // Vermont
  { code: '802', state: 'VT', region: 'Burlington / statewide' },
  // Virginia
  { code: '276', state: 'VA', region: 'Southwest Virginia' },
  { code: '434', state: 'VA', region: 'Charlottesville / Lynchburg' },
  { code: '540', state: 'VA', region: 'Roanoke / Fredericksburg' },
  { code: '571', state: 'VA', region: 'Northern Virginia' },
  { code: '703', state: 'VA', region: 'Northern Virginia' },
  { code: '757', state: 'VA', region: 'Norfolk / Virginia Beach' },
  { code: '804', state: 'VA', region: 'Richmond' },
  { code: '826', state: 'VA', region: 'Richmond' },
  { code: '948', state: 'VA', region: 'Norfolk / Virginia Beach' },
  // Washington
  { code: '206', state: 'WA', region: 'Seattle' },
  { code: '253', state: 'WA', region: 'Tacoma' },
  { code: '360', state: 'WA', region: 'Olympia / Bellingham' },
  { code: '425', state: 'WA', region: 'Bellevue / Everett' },
  { code: '509', state: 'WA', region: 'Spokane / eastern Washington' },
  { code: '564', state: 'WA', region: 'Olympia / western Washington' },
  // West Virginia
  { code: '304', state: 'WV', region: 'Charleston / statewide' },
  { code: '681', state: 'WV', region: 'Charleston / statewide' },
  // Wisconsin
  { code: '262', state: 'WI', region: 'Kenosha / Waukesha' },
  { code: '274', state: 'WI', region: 'Green Bay / Appleton' },
  { code: '414', state: 'WI', region: 'Milwaukee' },
  { code: '534', state: 'WI', region: 'Eau Claire / Wausau' },
  { code: '608', state: 'WI', region: 'Madison' },
  { code: '715', state: 'WI', region: 'Eau Claire / Wausau' },
  { code: '920', state: 'WI', region: 'Green Bay / Appleton' },
  // Wyoming
  { code: '307', state: 'WY', region: 'Cheyenne / statewide' },
];

// O(1) membership set — what the Zod refinement and the combobox's
// manual-entry validation actually consult.
export const US_AREA_CODE_SET: ReadonlySet<string> = new Set(
  US_AREA_CODES.map((entry) => entry.code),
);

export function isValidUsAreaCode(code: string): boolean {
  return US_AREA_CODE_SET.has(code);
}

// Shared cap on how many preferred prefixes one request may carry (PE-1766
// spec §5.1). One place so the FE row limit and the Zod max never drift.
export const MAX_PREFERRED_PREFIXES = 5;
